import React, { useState } from 'react';
import { Mail, Plus, ChevronDown, ChevronUp, Check, X, Link, Pencil, RefreshCw, Loader2 } from 'lucide-react';

/**
 * EmailAccountIcon - Icon based on account type
 */
const EmailAccountIcon = ({ type, size = 12 }) => {
  switch (type) {
    case 'gmail':
      return (
        <svg style={{ width: size, height: size }} viewBox="0 0 24 24">
          <path fill="currentColor" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
        </svg>
      );
    case 'icloud':
      return (
        <svg style={{ width: size, height: size }} viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
      );
    case 'nylas':
      return <Mail size={size} className="text-purple-400" />;
    default:
      return <Mail size={size} />;
  }
};

/**
 * EmailConnectionPanel - Collapsible panel for connecting email accounts with account tabs
 */
const EmailConnectionPanel = ({
  onAddAccount,
  connectedAccounts = [],
  // Email account tabs props
  emailAccounts = [],
  activeEmailAccount,
  onSelectAccount,
  onSyncAccount,
  onConnectAccount,
  onRemoveAccount,
  onDisconnectAll,
  renamingAccountId,
  renameValue,
  setRenameValue,
  onStartRename,
  onRename,
  onCancelRename,
  nylasLoading = false,
  disconnectingAll = false,
  className = ''
}) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      // Default to collapsed (true) unless user explicitly expanded it
      return localStorage.getItem('email_connection_panel_collapsed') !== 'false';
    } catch {
      return true;
    }
  });

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    try {
      localStorage.setItem('email_connection_panel_collapsed', String(newState));
    } catch {
      // Ignore storage errors
    }
  };

  const getTabStyle = (account, isActive) => {
    if (!isActive) return 'bg-gray-800/80 text-gray-400 hover:bg-gray-700 border border-gray-600';
    switch (account.type) {
      case 'outlook': return 'bg-blue-600 text-white border border-blue-500';
      case 'gmail': return 'bg-white text-black border border-white';
      case 'nylas': return 'bg-purple-600 text-white border border-purple-500';
      default: return 'bg-gray-600 text-white border border-gray-500';
    }
  };

  return (
    <div className={`mx-3 mt-2 mb-0 bg-[#0A1628] border border-emerald-500/50 rounded-xl transition-all duration-200 hover:border-emerald-400/70 shadow-[0_0_15px_rgba(16,185,129,0.15)] ${className}`}>
      {/* Header - Always visible */}
      <div 
        className="flex items-center justify-between p-2.5 cursor-pointer hover:bg-[#0D1D35] transition-colors rounded-t-xl"
        onClick={toggleCollapse}
        data-testid="email-connection-panel-header"
      >
        <div className="flex items-center gap-2">
          <Mail className="text-emerald-400" size={16} />
          <h3 className="text-sm font-bold text-white">Connect Email Accounts</h3>
          {connectedAccounts.length > 0 && (
            <span className="text-xs text-gray-300 bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-500/30">
              {connectedAccounts.filter(acc => acc.connected).length} connected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onAddAccount?.();
              }}
              data-testid="add-email-account-btn"
              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1 transition-all text-xs border border-emerald-500/30"
            >
              <Plus size={12} />
              Add
            </button>
          )}
          <button 
            className="p-1 hover:bg-[#0D1D35] rounded transition-colors"
            data-testid="email-connection-panel-toggle"
          >
            {isCollapsed ? (
              <ChevronDown size={18} className="text-emerald-400" />
            ) : (
              <ChevronUp size={18} className="text-emerald-400" />
            )}
          </button>
        </div>
      </div>
      
      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="px-2.5 pb-3 pt-0">
          <p className="text-gray-400 text-xs mb-3">Add multiple email accounts from Outlook, Gmail, or iCloud.</p>
          
          {/* Empty State - No email accounts yet */}
          {emailAccounts.length === 0 && (
            <div className="pt-2 border-t border-emerald-500/20">
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <Mail className="text-emerald-400/50 mb-2" size={32} />
                <p className="text-gray-400 text-sm mb-3">No email accounts connected yet</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddAccount?.();
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30"
                  data-testid="connect-first-email-btn"
                >
                  <Plus size={16} />
                  Connect Your First Email
                </button>
              </div>
            </div>
          )}
          
          {/* Email Account Tabs - Inside the dropdown */}
          {emailAccounts.length > 0 && (
            <div className="pt-2 border-t border-emerald-500/20">
              <p className="text-xs text-gray-400 mb-2">Your Email Accounts:</p>
              <div className="flex flex-wrap gap-2 pb-1">
                {emailAccounts.map((account) => {
                  const isActive = activeEmailAccount === account.id;
                  const isRenaming = renamingAccountId === account.id;
                  const isLoading = nylasLoading && isActive;
                  
                  if (isRenaming) {
                    return (
                      <div key={account.id} className="flex items-center gap-1 bg-gray-800 rounded-lg px-2 py-1.5">
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue?.(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') onRename?.();
                            else if (e.key === 'Escape') onCancelRename?.();
                          }}
                          className="bg-transparent border-none outline-none text-white text-xs w-20"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button onClick={(e) => { e.stopPropagation(); onRename?.(); }} className="text-green-400 hover:text-green-300">
                          <Check size={12} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onCancelRename?.(); }} className="text-red-400 hover:text-red-300">
                          <X size={12} />
                        </button>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={account.id} className="flex items-center gap-1 group">
                      {/* Account Tab Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectAccount?.(account.id);
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          onStartRename?.(account.id, account.name);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${getTabStyle(account, isActive)}`}
                        title="Click to select, double-click to rename"
                        data-testid={`email-tab-${account.id}`}
                      >
                        <EmailAccountIcon type={account.type} size={12} />
                        {account.name}
                        {account.connected && (
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" title="Connected"></span>
                        )}
                      </button>
                      
                      {/* Remove button - visible on hover */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveAccount?.(account.id);
                        }}
                        className="p-1 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-400 transition-all opacity-0 group-hover:opacity-100"
                        title={`Remove ${account.name}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  );
                })}
                
                {/* Sync button - syncs the currently active/selected account */}
                {emailAccounts.length > 0 && emailAccounts.some(acc => acc.connected) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
                      if (activeAccount?.connected) {
                        onSyncAccount?.(activeAccount);
                      }
                    }}
                    disabled={nylasLoading}
                    className={`p-2 rounded-lg transition-all flex items-center justify-center ${
                      nylasLoading 
                        ? 'bg-gray-700 cursor-not-allowed' 
                        : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white border border-gray-600'
                    }`}
                    title="Sync emails for selected account"
                    data-testid="sync-emails-btn"
                  >
                    {nylasLoading ? (
                      <Loader2 size={14} className="animate-spin text-purple-400" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                  </button>
                )}
                
                {/* Add Email Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddAccount?.();
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 bg-gray-800/50 text-gray-400 hover:bg-gray-700 hover:text-white border border-dashed border-gray-600"
                  title="Add new email account"
                  data-testid="add-email-tab-btn"
                >
                  <Plus size={12} />
                  Add Email
                </button>
              </div>
              
              {/* Disconnect All Button - Only show if there are connected accounts */}
              {emailAccounts.filter(acc => acc.connected && acc.type === 'nylas').length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-500/20">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDisconnectAll?.();
                    }}
                    disabled={disconnectingAll}
                    className="w-full px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Disconnect all email accounts"
                    data-testid="disconnect-all-emails-btn"
                  >
                    {disconnectingAll ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Disconnecting...
                      </>
                    ) : (
                      <>
                        <X size={14} />
                        Disconnect All Emails
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailConnectionPanel;
